import { formatMoney } from "@szerviz/core";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApp, useFeature } from "../context";
import type { Customer, Recommendation, Vehicle, WorkOrder, WorkOrderItem } from "../data";
import { ErrorText } from "./ErrorText";

/**
 * Recommended work the customer declined earlier, kept on the vehicle until it is done
 * (added to a later work order) or no longer relevant. On an open work order of the
 * vehicle the lines can be taken over with one tap.
 */
export function Recommendations({ vehicleId, order, onAdded }: { vehicleId: string; order?: WorkOrder; onAdded?: () => Promise<void> }) {
  const { t, i18n } = useTranslation();
  const { store, workshop } = useApp();
  const quotesOn = useFeature("quotes");
  const [rows, setRows] = useState<Recommendation[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const load = useCallback(async () => {
    const all = await store.vehicleRecommendations(vehicleId);
    // the work order's own declined lines are shown in its item list
    setRows(order ? all.filter((r) => r.workOrderId !== order.id) : all);
  }, [store, vehicleId, order]);
  useEffect(() => void load(), [load]);
  if (rows.length === 0) return null;

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    setBusy(true);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  const canAdd = !!order && order.status !== "closed";
  const add = (r: Recommendation) =>
    run(async () => {
      // with quotes on, the customer is asked again
      await store.saveWorkOrderItem(workshop.id, order!.id, { ...r.line, approval: quotesOn ? "pending" : "none" });
      await store.closeRecommendation(r.itemId, order!.id);
      await onAdded?.();
    });
  const drop = (r: Recommendation) => {
    if (globalThis.confirm?.(t("recommend.dropConfirm", { item: r.line.description }))) void run(() => store.closeRecommendation(r.itemId, null));
  };

  return (
    <section className="recommendations no-print" aria-label={t("recommend.title")}>
      <h2>{t("recommend.title")}</h2>
      <p className="muted">{t("recommend.intro")}</p>
      <ErrorText error={error} />
      <ul>
        {rows.map((r) => (
          <li key={r.itemId}>
            <div className="rec-text">
              <strong>{r.line.description}</strong>
              <span>{formatMoney(r.gross, workshop.currency, i18n.language)}</span>
              <small className="muted">
                {t("recommend.declinedOn", { date: new Date(r.declinedOn).toLocaleDateString(i18n.language), number: r.workOrderNumber })}
              </small>
            </div>
            <div className="actions">
              {canAdd && (
                <button type="button" className="primary" disabled={busy} onClick={() => void add(r)}>
                  {t("recommend.add")}
                </button>
              )}
              <button type="button" disabled={busy} onClick={() => drop(r)}>
                {t("recommend.drop")}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** E-mail to the customer listing the lines they declined on this work order (feature, once). */
export function DeclinedWorkEmail({ order, items, customer, vehicle }: { order: WorkOrder; items: WorkOrderItem[]; customer: Customer | null; vehicle: Vehicle | null }) {
  const { t, i18n } = useTranslation();
  const { store, workshop } = useApp();
  const on = useFeature("declinedWorkEmail");
  const [sentAt, setSentAt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const declined = items.filter((i) => i.approval === "declined");
  useEffect(() => {
    if (on) void store.declinedWorkSent(order.id).then(setSentAt);
  }, [on, store, order.id]);
  if (!on || declined.length === 0) return null;

  if (sentAt) {
    return <p className="muted no-print">{t("recommend.emailSent", { date: new Date(sentAt).toLocaleDateString(i18n.language) })}</p>;
  }
  const email = customer?.email?.trim();
  // replies go to the workshop's e-mail (Reply-To), so the text only asks for a reply when one is set (#160)
  const contact = (key: string) => {
    const v = workshop.general[key];
    return typeof v === "string" ? v.trim() : "";
  };
  const workshopEmail = contact("workshopEmail").includes("@") ? contact("workshopEmail") : "";
  const phone = contact("workshopPhone");
  const ask = workshopEmail ? (phone ? t("recommend.askReplyCall", { phone }) : t("recommend.askReply")) : phone ? t("recommend.askCall", { phone }) : t("recommend.ask");
  const signature = [workshop.name, contact("workshopAddress"), phone, workshopEmail].filter(Boolean).join("\n");
  const send = async () => {
    setError(null);
    setBusy(true);
    try {
      const lines = declined.map((i) => `• ${i.description}: ${formatMoney(i.gross, workshop.currency, i18n.language)}`).join("\n");
      await store.sendDeclinedWork(order.id, {
        subject: t("recommend.emailSubject", { plate: vehicle?.plateNumber ?? "" }),
        text: t("recommend.emailText", { customer: customer?.name ?? "", plate: vehicle?.plateNumber ?? "", lines, ask, workshop: signature }),
      });
      setSentAt(new Date().toISOString());
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="declined-email actions no-print">
      <ErrorText error={error} />
      <button type="button" disabled={busy || !email} onClick={() => void send()}>
        {t("recommend.emailButton", { count: declined.length })}
      </button>
      {!email && <span className="missing">{t("recommend.emailMissing")}</span>}
      {email && !workshopEmail && <small className="warn-text">{t("recommend.noWorkshopEmail")}</small>}
    </div>
  );
}
